import React, { Dispatch, SetStateAction, useState } from 'react';
import VoxelAlert from '../../../../components/voxel/Alert/VoxelAlert';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelSelect, { GenericSelectOption } from '../../../../components/voxel/Forms/VoxelSelect';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import { TagGroupAutoColumnGq } from '../../../../graphql/projects/models/TagGroupAutoColumnGq';
import { TagGroupAutoHandleConflictsGq } from '../../../../graphql/projects/models/TagGroupAutoHandleConflictsGq';
import { TagGroupAutoOverwriteGq } from '../../../../graphql/projects/models/TagGroupAutoOverwriteGq';
import { TagGroupAutoTypeGq } from '../../../../graphql/projects/models/TagGroupAutoTypeGq';
import { TagGroupGq } from '../../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';

export interface AutotagDialogProps {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  tagGroup: TagGroupGq;
  hasEnglishTranslation: boolean;
  hasTranslation: boolean;
  reload: () => Promise<unknown>;
  onSuccess?: () => void;
}

const AutotagDialog: React.FC<AutotagDialogProps> = ({
  openDialog = true,
  setOpenDialog,
  tagGroup,
  hasEnglishTranslation,
  hasTranslation,
  reload,
  onSuccess,
}) => {
  const textMatchOptions: GenericSelectOption[] = [
    {
      value: TagGroupAutoTypeGq.ANYWHERE,
      title: 'Anywhere in keyword',
      subtitle: 'The tag can match any part of the keyword. E.g. "phone" in "iphone"',
    },
    {
      value: TagGroupAutoTypeGq.STANDALONE_WORDS,
      title: 'Standalone words in keyword',
      subtitle:
        'The tag can match a standalone word of the keyword. E.g. "phone" in "large phone" but not in "iphone"',
    },
    {
      value: TagGroupAutoTypeGq.EXACT_KEYWORD,
      title: 'Exact match',
      subtitle: 'The tag is exactly the same as the keyword',
    },
  ];

  const columnOptions: GenericSelectOption[] = [
    {
      value: TagGroupAutoColumnGq.KEYWORD,
      title: 'Keyword',
    },
    {
      value: TagGroupAutoColumnGq.TRANSLATION_ENGLISH,
      title: 'English',
      disabled: !hasEnglishTranslation,
    },
    {
      value: TagGroupAutoColumnGq.TRANSLATED_FROM_KEYWORD,
      title: 'Translated from',
      disabled: !hasTranslation,
    },
  ];
  const keywordOptions: GenericSelectOption[] = [
    {
      value: TagGroupAutoOverwriteGq.UNTAGGED_KEYWORDS_ONLY,
      title: 'Untagged keywords only',
      subtitle: 'None of the already existing tags will be replaced with a new tag result',
    },
    {
      value: TagGroupAutoOverwriteGq.OVERWRITE_TAGGED_KEYWORDS,
      title: 'All keywords',
      subtitle:
        'In case of a match with a keyword, any existing tag will be replaced with the new tag result',
    },
    {
      value: TagGroupAutoOverwriteGq.CLEAN_ALL_TAGS_AND_AUTOTAG,
      title: 'Clear and start over from scratch',
      subtitle: 'Clear all existing tags all from keywords and start over from scratch',
    },
  ];
  const conflictOptions: GenericSelectOption[] = [
    {
      value: TagGroupAutoHandleConflictsGq.FIRST_OCCURANCE,
      title: 'Resolve automatically',
      subtitle: 'Automatically selects one of the relevant tags',
    },
    {
      value: TagGroupAutoHandleConflictsGq.NONE,
      title: 'Solve manually',
      subtitle: 'All conflicts will be listed and solved manually at a later stage',
    },
  ];

  const [autotagType, setAutotagType] = useState<TagGroupAutoTypeGq>(TagGroupAutoTypeGq.ANYWHERE);
  const [column, setColumn] = useState<TagGroupAutoColumnGq>(TagGroupAutoColumnGq.KEYWORD);

  const [keywords, setKeywords] = useState<TagGroupAutoOverwriteGq>(
    TagGroupAutoOverwriteGq.UNTAGGED_KEYWORDS_ONLY
  );

  const [handleConflicts, setHandleConflicts] = useState<TagGroupAutoHandleConflictsGq>(
    TagGroupAutoHandleConflictsGq.FIRST_OCCURANCE
  );

  const applyAutoTagMutation = projectsApiService.mutations.tags.useQueueAutoTagGq();
  const [loading, setLoading] = useState(false);
  const applyAutoTag = () => {
    setLoading(true);
    return applyAutoTagMutation({
      tagGroupId: tagGroup.id,
      autotagType: autotagType,
      autotagColumn: column,
      autotagOverwrite: keywords,
      autotagHandleConflicts: handleConflicts,
      projectId: tagGroup.projectId,
    })
      .then(reload)
      .finally(() => {
        setLoading(false);
        setOpenDialog(false);
        onSuccess && onSuccess();
      });
  };

  return (
    <VoxelDialog
      title="Tag keywords automatically"
      minWClass="min-w-[800px]"
      open={openDialog}
      onClose={() => setOpenDialog(false)}
    >
      <VoxelDialogContent classMinWidth="flex flex-col gap-8">
        <VoxelText>
          Tag keywords based on a text match between the keyword and the tag (including grammar
          variants).
        </VoxelText>
        {(hasEnglishTranslation || hasTranslation) && (
          <div className="flex flex-row items-center px-2">
            <div className="basis-1/4">
              <VoxelText variant="bread-semibold" className="align-middle">
                Column
              </VoxelText>
            </div>
            <div className="basis-3/4 w-full">
              <VoxelSelect
                selectedOptionValue={column}
                setSelectedOption={(event) => setColumn(event.value)}
                options={columnOptions}
                hideDisabledOptions
              />
            </div>
          </div>
        )}
        <div className="flex flex-row items-center px-2">
          <div className="basis-1/4">
            <VoxelText variant="bread-semibold" className="align-middle">
              Tag match
            </VoxelText>
          </div>
          <div className="basis-3/4 w-full">
            <VoxelSelect
              selectedOptionValue={autotagType}
              setSelectedOption={(event) => setAutotagType(event.value)}
              options={textMatchOptions}
            />
          </div>
        </div>
        <div className="flex flex-row items-center px-2">
          <div className="basis-1/4">
            <VoxelText variant="bread-semibold" className="align-middle">
              Keywords
            </VoxelText>
          </div>
          <div className="basis-3/4 w-full">
            <VoxelSelect
              selectedOptionValue={keywords}
              setSelectedOption={(event) => setKeywords(event.value)}
              options={keywordOptions}
            />
          </div>
        </div>
        <div className="flex flex-row items-center px-2">
          <div className="basis-1/4">
            <VoxelText variant="bread-semibold" className="align-middle">
              Conflicts
            </VoxelText>
          </div>
          <div className="basis-3/4 w-full relative">
            <VoxelSelect
              selectedOptionValue={handleConflicts}
              setSelectedOption={(event) => setHandleConflicts(event.value)}
              options={conflictOptions}
            />
            <div className="pt-2">
              <VoxelText variant="form-caption">
                When multiple tags match the same keyword
              </VoxelText>
            </div>
          </div>
        </div>
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={() => setOpenDialog(false)} disabled={loading} />
        <VoxelDialogActionSubmit
          onClick={applyAutoTag}
          title={'Confirm'}
          disabled={loading || !tagGroup?.tags?.length}
        />
      </VoxelDialogActions>
      {!tagGroup?.tags?.length && (
        <VoxelAlert roundedClass="rounded-t-none" severity="error" size="sm" iconSize="lg">
          Tag group must contain tags to make use of this feature.
        </VoxelAlert>
      )}
    </VoxelDialog>
  );
};

export default AutotagDialog;
